import type { ChatMessage } from "../types/chat";

/**
 * Response structure for parsed AI responses in drafting mode.
 * The AI outputs responses with two parts: conversation and draft.
 */
export interface ParsedDraftingResponse {
  conversation: string;
  draft: string;
}

/**
 * Parses a drafting mode response that contains both conversation and draft sections.
 *
 * Expected format:
 * <conversation>
 * Conversational text here...
 * </conversation>
 *
 * <draft>
 * # Besluit ...
 * Markdown content here...
 * </draft>
 */
export function parseDraftingResponse(content: string): ParsedDraftingResponse {
  // Normalize content: remove potential markdown code block wrapping around tags
  // AI sometimes outputs tags inside code blocks
  let normalizedContent = content;

  // Handle case where tags might be inside a code block
  const codeBlockMatch = content.match(/```(?:xml|markdown|md)?\s*\n?([\s\S]*?)```/);
  if (codeBlockMatch?.[1]?.includes("<conversation>")) {
    normalizedContent = codeBlockMatch[1];
  }

  // Extract conversation section - handle various whitespace patterns
  const conversationMatch = normalizedContent.match(
    /<conversation>\s*([\s\S]*?)\s*<\/conversation>/i,
  );
  const conversation = conversationMatch?.[1]?.trim() ?? "";

  // Extract draft section - handle various whitespace patterns
  const draftMatch = normalizedContent.match(/<draft>\s*([\s\S]*?)\s*<\/draft>/i);
  const draft = draftMatch?.[1]?.trim() ?? "";

  // If no structured format found, fall back to legacy extraction
  if (!conversationMatch && !draftMatch) {
    // Try to extract draft using legacy patterns
    const legacyDraft = extractDraftFromContentLegacy(content);
    if (legacyDraft) {
      // Remove the draft from the content to get conversation
      const conversationText = content
        .replace(/```(?:markdown|md)?\s*\n[\s\S]*?```/g, "")
        .replace(/(^|\n)#\s+(?:Besluit|Concept)[\s\S]*$/i, "")
        .trim();
      return {
        conversation: conversationText || content,
        draft: legacyDraft,
      };
    }
    // No draft found - entire content is conversation
    return { conversation: content, draft: "" };
  }

  // If we found draft but conversation is empty, extract everything before <draft> tag as conversation
  if (draft && !conversation) {
    const beforeDraft = normalizedContent.split(/<draft>/i)[0]?.trim() ?? "";
    // Remove any leftover tags
    const cleanedConversation = beforeDraft
      .replace(/<\/?conversation>/gi, "")
      .replace(/<\/?draft>/gi, "")
      .trim();
    return { conversation: cleanedConversation, draft };
  }

  return { conversation, draft };
}

/**
 * Extracts only the conversation part from a drafting response.
 * This is what should be displayed in the chat interface.
 */
export function extractConversationFromContent(content: string): string {
  return parseDraftingResponse(content).conversation;
}

/**
 * Extracts only the draft part from a drafting response.
 * This is what should be displayed in the preview panel.
 */
export function extractDraftFromContent(content: string): string {
  return parseDraftingResponse(content).draft;
}

/**
 * Extracts the most recent draft from all messages.
 * Looks through messages from newest to oldest.
 */
export function extractDraftFromMessages(messages: ChatMessage[]): string {
  // Look through messages from newest to oldest
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (!message) continue;

    // Only check assistant messages
    if (message.role !== "assistant") {
      continue;
    }

    // Try to extract a draft from this message
    const draft = extractDraftFromContent(message.content);
    if (draft) {
      return draft;
    }
  }

  return "";
}

/**
 * Legacy extraction for backward compatibility with responses
 * that don't use the new structured format.
 */
function extractDraftFromContentLegacy(content: string): string {
  // Pattern 1: Look for markdown code blocks
  const codeBlockMatch = content.match(/```(?:markdown|md)?\s*\n([\s\S]*?)```/);
  if (codeBlockMatch?.[1]) {
    const blockContent = codeBlockMatch[1].trim();
    // Verify it looks like a besluit (starts with heading containing "Besluit")
    if (blockContent.match(/^#\s+(?:Besluit|Concept)/i)) {
      return blockContent;
    }
    // Also accept if it starts with any markdown heading
    if (blockContent.startsWith("#")) {
      return blockContent;
    }
  }

  // Pattern 2: Look for besluit structure without code block
  const besluitMatch = content.match(
    /(^|\n)(#\s+(?:Besluit|Concept)[^\n]*\n(?:[\s\S]*?)(?=\n\n---|\n\nHeb je|$))/i,
  );
  if (besluitMatch?.[2]) {
    return besluitMatch[2].trim();
  }

  // Pattern 3: Look for any heading followed by besluit structure
  const headingMatch = content.match(
    /(^|\n)(#\s+[^\n]+\n+(?:\*\*Datum\*\*|\*\*Status\*\*|##\s+)[\s\S]*?)(?=\n\n(?:---|\*\*|Heb je|Is er|Wil je)|$)/i,
  );
  if (headingMatch?.[2]) {
    return headingMatch[2].trim();
  }

  return "";
}
